import { useCallback, useMemo } from 'react';
import { useSettings } from '../../../contexts/SettingsContext/hooks/useSettings';
import { useErrorHandler } from '../../../hooks/useErrorHandler';
import type { ToastHelpers } from '../../../hooks/useToast';

interface UseDefaultMeetingParams {
  toastHelpers: ToastHelpers;
}

interface UseDefaultMeetingReturn {
  defaultMeetingId: string | null;
  setDefaultMeetingId: (meetingId: string | undefined, meetingPasscode?: string) => Promise<void>;
  isDefaultMeeting: (meetingId: string) => boolean;
}

const useDefaultMeeting = ({ toastHelpers }: UseDefaultMeetingParams): UseDefaultMeetingReturn => {
  const { profile, updateProfile } = useSettings();
  const { handleComponentError } = useErrorHandler('PersonalMeetingsModal');

  const defaultMeetingId = useMemo(() => profile?.defaultMeetingId ?? null, [profile]);

  const isDefaultMeeting = useCallback(
    (meetingId: string): boolean => {
      return defaultMeetingId === meetingId;
    },
    [defaultMeetingId],
  );

  const setDefaultMeetingId = useCallback(
    async (meetingId: string | undefined, meetingPasscode?: string) => {
      if (!profile) {
        toastHelpers.showError('Profile not loaded', 'Cannot update default meeting');
        return;
      }

      try {
        await updateProfile({
          ...profile,
          defaultMeetingId: meetingId,
          defaultMeetingPasscode: meetingPasscode ?? '',
        });

        if (meetingId) {
          toastHelpers.showSuccess('Default Personal Meeting ID set');
        } else {
          toastHelpers.showSuccess('Default Personal Meeting ID removed');
        }
      } catch (err) {
        handleComponentError(err as Error, 'set_default_meeting');
        toastHelpers.showError('Unable to update default meeting', (err as Error).message);
      }
    },
    [profile, updateProfile, toastHelpers, handleComponentError],
  );

  return {
    defaultMeetingId,
    setDefaultMeetingId,
    isDefaultMeeting,
  };
};

export default useDefaultMeeting;
